import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  CalendarIcon,
  Plus,
  Wallet,
  Loader2,
  Trash2,
  ArrowDownRight,
  Banknote,
  Pencil,
  Building,
  Shield,
  Settings
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MoneyMovementPage = () => {
  const { isAdmin, isManager } = useAuth();
  const { stores, selectedStore } = useStore();
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [transfers, setTransfers] = useState([]);
  const [transferTypes, setTransferTypes] = useState([]);
  const [cashBalance, setCashBalance] = useState(null);
  const [bankBalance, setBankBalance] = useState(null);
  const [safeBalance, setSafeBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingTransfer, setEditingTransfer] = useState(null);
  const [storeDetails, setStoreDetails] = useState(null);

  const [formData, setFormData] = useState({
    date: new Date(),
    transfer_type: '',
    source: 'cash_holding',
    amount: '',
    description: '',
    reference: '',
    target_bank_id: ''  // For bank deposits
  });

  const [newTypeForm, setNewTypeForm] = useState({
    name: ''
  });

  useEffect(() => {
    fetchTransferTypes();
  }, []);

  useEffect(() => {
    if (stores.length > 0) {
      const storeId = selectedStore?.id || stores[0].id;
      setSelectedStoreId(storeId);
    }
  }, [stores, selectedStore]);

  useEffect(() => {
    if (selectedStoreId) {
      fetchTransfers();
      fetchAllBalances();
      fetchStoreDetails();
    }
  }, [selectedStoreId]);

  const fetchTransferTypes = async () => {
    try {
      const response = await axios.get(`${API}/settings/transfer-types`);
      setTransferTypes(response.data.filter(t => t.is_active));
    } catch (error) {
      console.error('Failed to load transfer types');
    }
  };

  const fetchStoreDetails = async () => {
    try {
      const response = await axios.get(`${API}/stores/${selectedStoreId}`);
      setStoreDetails(response.data);
    } catch (error) {
      console.error('Failed to load store details');
    }
  };

  const fetchTransfers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/money-movements`, {
        params: { store_id: selectedStoreId }
      });
      setTransfers(response.data);
    } catch (error) {
      toast.error('Failed to load transfers');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllBalances = async () => {
    try {
      const [cashRes, bankRes, safeRes] = await Promise.all([
        axios.get(`${API}/money-movements/cash-balance/${selectedStoreId}`),
        axios.get(`${API}/money-movements/bank-balance/${selectedStoreId}`),
        axios.get(`${API}/money-movements/safe-balance/${selectedStoreId}`)
      ]);
      setCashBalance(cashRes.data);
      setBankBalance(bankRes.data);
      setSafeBalance(safeRes.data);
    } catch (error) {
      console.error('Failed to load balances');
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date(),
      transfer_type: '',
      source: 'cash_holding',
      amount: '',
      description: '',
      reference: '',
      target_bank_id: ''
    });
    setEditingTransfer(null);
  };

  const openAddDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (transfer) => {
    setEditingTransfer(transfer);
    setFormData({
      date: new Date(transfer.date),
      transfer_type: transfer.transfer_type,
      source: transfer.source || 'cash_holding',
      amount: transfer.amount.toFixed(2),
      description: transfer.description || '',
      reference: transfer.reference || '',
      target_bank_id: transfer.target_bank_id || ''
    });
    setDialogOpen(true);
  };

  // Filter transfer types based on source selection
  const getFilteredTransferTypes = () => {
    if (formData.source === 'safe') {
      // When source is Safe, show all types except "Safe Transfer" (can't transfer to safe from safe)
      return transferTypes.filter(t => {
        const name = t.name.toLowerCase().trim();
        return name !== 'safe transfer' && name !== 'to safe';
      });
    }
    // When source is Cash Holding, show all types except those that transfer TO cash holding
    return transferTypes.filter(t => {
      const name = t.name.toLowerCase().trim();
      return !name.includes('to cash') && !name.includes('cash holding');
    });
  };

  const validateForm = () => {
    if (!formData.transfer_type) {
      toast.error('Please select a transfer type');
      return false;
    }
    // Require bank selection for bank deposits
    if (formData.transfer_type.toLowerCase().includes('bank deposit') && !formData.target_bank_id) {
      toast.error('Please select a bank account');
      return false;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return false;
    }
    if (!formData.description.trim()) {
      toast.error('Please enter a description');
      return false;
    }
    if (!formData.reference.trim()) {
      toast.error('Please enter a reference');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setSaving(true);

    try {
      const payload = {
        ...formData,
        store_id: selectedStoreId,
        date: format(formData.date, 'yyyy-MM-dd'),
        amount: parseFloat(parseFloat(formData.amount).toFixed(2))
      };

      if (editingTransfer) {
        await axios.put(`${API}/money-movements/${editingTransfer.id}`, payload);
        toast.success('Transfer updated successfully!');
      } else {
        await axios.post(`${API}/money-movements`, payload);
        toast.success('Transfer recorded successfully!');
      }
      
      setDialogOpen(false);
      fetchTransfers();
      fetchAllBalances();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save transfer');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (transferId) => {
    if (!window.confirm('Are you sure you want to delete this transfer?')) return;
    
    try {
      await axios.delete(`${API}/money-movements/${transferId}`);
      toast.success('Transfer deleted');
      fetchTransfers();
      fetchAllBalances();
    } catch (error) {
      toast.error('Failed to delete transfer');
    }
  };

  const handleAddType = async () => {
    if (!newTypeForm.name.trim()) {
      toast.error('Please enter a type name');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/settings/transfer-types`, {
        name: newTypeForm.name.trim()
      });
      toast.success('Transfer type added!');
      setTypeDialogOpen(false);
      setNewTypeForm({ name: '' });
      fetchTransferTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add transfer type');
    } finally {
      setSaving(false);
    }
  };

  const getTotalTransfers = () => {
    return transfers.reduce((sum, t) => sum + (t.amount || 0), 0);
  };

  return (
    <div className="space-y-6" data-testid="cash-control-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
            Cash Control
          </h1>
          <p className="text-muted-foreground mt-1">
            Track cash transfers, bank deposits, and safe management
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Select value={selectedStoreId} onValueChange={setSelectedStoreId}>
            <SelectTrigger className="w-[200px]" data-testid="store-select">
              <SelectValue placeholder="Select store" />
            </SelectTrigger>
            <SelectContent>
              {stores.map(store => (
                <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button onClick={openAddDialog} data-testid="add-transfer-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Transfer
          </Button>
        </div>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Cash Balance */}
        {cashBalance && (
          <Card className="bg-gradient-to-r from-green-500/10 to-transparent border-green-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Cash Holding</p>
                  <p className="text-3xl font-bold font-mono mt-1 text-green-400">
                    £{cashBalance.current_balance.toFixed(2)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    From sales: £{(cashBalance.total_cash_in || 0).toFixed(2)}
                  </p>
                </div>
                <div className="p-4 rounded-2xl bg-green-500/20">
                  <Banknote className="h-8 w-8 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bank Balance - Show all bank accounts */}
        {bankBalance && bankBalance.bank_accounts && bankBalance.bank_accounts.length > 0 ? (
          bankBalance.bank_accounts.map((account, idx) => (
            <Card key={account.id || idx} className="bg-gradient-to-r from-blue-500/10 to-transparent border-blue-500/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{account.name || 'Bank Account'}</p>
                    <p className="text-3xl font-bold font-mono mt-1 text-blue-400">
                      £{(account.balance || 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="p-4 rounded-2xl bg-blue-500/20">
                    <Building className="h-8 w-8 text-blue-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : bankBalance && (
          <Card className="bg-gradient-to-r from-blue-500/10 to-transparent border-blue-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Bank Balance</p>
                  <p className="text-3xl font-bold font-mono mt-1 text-blue-400">
                    £{bankBalance.current_balance.toFixed(2)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Card payments: £{(bankBalance.total_card_payments || 0).toFixed(2)}
                  </p>
                </div>
                <div className="p-4 rounded-2xl bg-blue-500/20">
                  <Building className="h-8 w-8 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Safe Balance */}
        {safeBalance && (
          <Card className="bg-gradient-to-r from-amber-500/10 to-transparent border-amber-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Safe Balance</p>
                  <p className="text-3xl font-bold font-mono mt-1 text-amber-400">
                    £{safeBalance.current_balance.toFixed(2)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Available for lending
                  </p>
                </div>
                <div className="p-4 rounded-2xl bg-amber-500/20">
                  <Shield className="h-8 w-8 text-amber-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="card-hover">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Transfers Out</p>
                <p className="text-2xl font-bold font-mono text-red-400">
                  £{getTotalTransfers().toFixed(2)}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-red-500/20">
                <ArrowDownRight className="h-6 w-6 text-red-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">This Month</p>
                <p className="text-2xl font-bold">{transfers.length} transfers</p>
              </div>
              <div className="p-3 rounded-xl bg-primary/20">
                <Wallet className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Transfers Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner" />
            </div>
          ) : transfers.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <Wallet className="h-12 w-12 mb-4 opacity-50" />
              <p>No transfers recorded</p>
              <Button variant="outline" className="mt-4" onClick={openAddDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Record First Transfer
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left p-4 font-medium text-muted-foreground">ID</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Date</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Type</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Description</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">Amount</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">By</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {transfers.map((transfer) => (
                    <tr 
                      key={transfer.id} 
                      className="border-b border-border/50 table-row-hover cursor-pointer"
                      onClick={() => openEditDialog(transfer)}
                      data-testid={`transfer-row-${transfer.id}`}
                    >
                      <td className="p-4 font-mono text-xs text-primary">
                        {transfer.transaction_id || '-'}
                      </td>
                      <td className="p-4">
                        {new Date(transfer.date).toLocaleDateString('en-GB')}
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          transfer.transfer_type?.toLowerCase().includes('bank') 
                            ? 'bg-blue-500/20 text-blue-400'
                            : transfer.transfer_type?.toLowerCase().includes('safe')
                            ? 'bg-amber-500/20 text-amber-400'
                            : 'bg-primary/20 text-primary'
                        }`}>
                          {transfer.transfer_type}
                        </span>
                      </td>
                      <td className="p-4 text-muted-foreground max-w-xs truncate">
                        {transfer.description || '-'}
                      </td>
                      <td className="p-4 text-right font-mono font-semibold text-red-400">
                        -£{(transfer.amount || 0).toFixed(2)}
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {transfer.created_by_name || '-'}
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditDialog(transfer);
                            }}
                            data-testid={`edit-transfer-${transfer.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(transfer.id);
                              }}
                              className="text-destructive"
                              data-testid={`delete-transfer-${transfer.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Transfer Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              {editingTransfer ? 'Edit Transfer' : 'Record Money Transfer'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Balance Display based on source */}
            <div className="p-4 bg-muted/50 rounded-lg space-y-2">
              {cashBalance && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Cash Holding:</span>
                  <span className={`font-bold font-mono ${formData.source === 'cash_holding' ? 'text-green-400' : ''}`}>
                    £{cashBalance.current_balance.toFixed(2)}
                  </span>
                </div>
              )}
              {safeBalance && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Safe Balance:</span>
                  <span className={`font-bold font-mono ${formData.source === 'safe' ? 'text-amber-400' : ''}`}>
                    £{safeBalance.current_balance.toFixed(2)}
                  </span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Transfer From *</Label>
              <Select 
                value={formData.source} 
                onValueChange={(value) => setFormData({ ...formData, source: value, transfer_type: '' })}
              >
                <SelectTrigger data-testid="source-select">
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash_holding">
                    <div className="flex items-center gap-2">
                      <Banknote className="h-4 w-4 text-green-400" />
                      Cash Holding
                    </div>
                  </SelectItem>
                  <SelectItem value="safe">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-amber-400" />
                      Safe
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(formData.date, 'PPP')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={formData.date}
                      onSelect={(date) => date && setFormData({ ...formData, date })}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Transfer Type *</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={() => setTypeDialogOpen(true)}
                  >
                    <Settings className="h-3 w-3 mr-1" />
                    Add Type
                  </Button>
                </div>
                <Select 
                  value={formData.transfer_type} 
                  onValueChange={(value) => setFormData({ ...formData, transfer_type: value })}
                >
                  <SelectTrigger data-testid="transfer-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {getFilteredTransferTypes().map(type => (
                      <SelectItem key={type.id} value={type.name}>{type.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Bank Account Selection - Show when Bank Deposit is selected */}
            {formData.transfer_type.toLowerCase().includes('bank deposit') && storeDetails?.bank_accounts?.length > 0 && (
              <div className="space-y-2">
                <Label>Select Bank Account *</Label>
                <Select 
                  value={formData.target_bank_id} 
                  onValueChange={(value) => setFormData({ ...formData, target_bank_id: value })}
                >
                  <SelectTrigger data-testid="bank-account-select">
                    <SelectValue placeholder="Select bank account" />
                  </SelectTrigger>
                  <SelectContent>
                    {storeDetails.bank_accounts.map(account => (
                      <SelectItem key={account.id} value={account.id}>
                        <div className="flex items-center justify-between w-full">
                          <span>{account.name}</span>
                          <span className="text-muted-foreground ml-4">£{(account.balance || 0).toFixed(2)}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Amount (£) *</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="font-mono text-lg"
                placeholder="0.00"
                data-testid="transfer-amount"
              />
              {formData.amount && parseFloat(formData.amount) > 0 && !editingTransfer && (
                <p className="text-sm text-muted-foreground">
                  {formData.source === 'cash_holding' && cashBalance && (
                    <>Balance after transfer: £{(cashBalance.current_balance - parseFloat(formData.amount)).toFixed(2)}</>
                  )}
                  {formData.source === 'safe' && safeBalance && (
                    <>Balance after transfer: £{(safeBalance.current_balance - parseFloat(formData.amount)).toFixed(2)}</>
                  )}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter description..."
                rows={2}
                data-testid="transfer-description"
              />
            </div>

            <div className="space-y-2">
              <Label>Reference *</Label>
              <Input
                value={formData.reference}
                onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                placeholder="Bank reference, receipt number, etc."
                data-testid="transfer-reference"
              />
            </div>
          </form>

          <DialogFooter className="pt-4">
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={saving} data-testid="save-transfer-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingTransfer ? 'Update Transfer' : 'Record Transfer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Transfer Type Dialog */}
      <Dialog open={typeDialogOpen} onOpenChange={setTypeDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>Add Transfer Type</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Type Name *</Label>
              <Input
                value={newTypeForm.name}
                onChange={(e) => setNewTypeForm({ name: e.target.value })}
                placeholder="e.g., Bank Deposit, Safe Transfer"
                data-testid="new-type-name"
              />
            </div>
          </div>

          <DialogFooter className="pt-4">
            <Button variant="outline" onClick={() => setTypeDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddType} disabled={saving} data-testid="save-type-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Add Type
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MoneyMovementPage;
